import React from 'react';
import { ArrowLeft, Shield } from 'lucide-react';
import { Language, t } from '../utils/i18n';

interface PrivacyPageProps {
  onBackToHome: () => void;
  lang: Language;
}

export default function PrivacyPage({ onBackToHome, lang }: PrivacyPageProps) {
  return (
    <div className="min-h-screen bg-[#F5F7FA] dark:bg-[#1E1E2E]">
      {/* 顶部导航栏 */}
      <header className="bg-white dark:bg-[#2D2D3F] shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={onBackToHome}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {t('backToHome', lang)}
            </button>
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-[#2D2D3F] rounded-xl shadow-lg p-6 sm:p-10 lg:p-16">
          {/* 标题区域 */}
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <Shield className="w-8 h-8 text-[#165DFF] flex-shrink-0" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100">
              {t('privacyTitle', lang)}
            </h1>
          </div>

          {/* 介绍 */}
          <p className="text-gray-700 dark:text-gray-300 mb-2">
            {t('privacyIntro', lang)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            {t('privacyLastUpdated', lang)}
          </p>

          {/* 1. 数据收集 */}
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3">
            {t('privacyDataCollection', lang)}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {t('privacyDataCollectionDesc', lang)}
          </p>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mt-6 mb-3">
            {t('privacyLocalStorage', lang)}
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            {t('privacyLocalStorageDesc', lang)}
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
            <li>{t('privacySearchHistory', lang)}</li>
            <li>{t('privacyFavorites', lang)}</li>
            <li>{t('privacyLanguage', lang)}</li>
            <li>{t('privacyRateLimit', lang)}</li>
            <li>{t('privacySparkData', lang)}</li>
          </ul>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {t('privacySparkDataDesc', lang)}
          </p>

          {/* 2. API调用 */}
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3">
            {t('privacyApiUsage', lang)}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {t('privacyApiUsageDesc', lang)}
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-6">
            <li>
              {lang === 'zh' ? '查询首先检查本地缓存，命中则直接返回（不消耗API配额）' : 'Queries first check local cache; cache hits return immediately (no API quota used)'}
            </li>
            <li>
              {lang === 'zh' ? '缓存未命中时调用LLM API处理查询并返回结果' : 'Cache misses call LLM API to process queries and return results'}
            </li>
            <li>
              {lang === 'zh' ? '查询内容会发送至LLM API提供商进行处理' : 'Query content is sent to LLM API providers for processing'}
            </li>
            <li>
              {lang === 'zh' ? '每日API调用限额为30次，缓存查询不受此限制' : 'Daily API call limit is 30; cache queries are unlimited'}
            </li>
          </ul>

          {/* 3. 数据存储位置 */}
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3">
            {t('privacyDataStorage', lang)}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {t('privacyDataStorageDesc', lang)}
          </p>

          {/* 4. 数据共享 */}
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3">
            {t('privacyDataSharing', lang)}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {t('privacyDataSharingDesc', lang)}
          </p>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mt-6 mb-3">
            {t('privacyThirdParty', lang)}
          </h3>
          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line mb-6">
            {t('privacyThirdPartyDesc', lang)}
          </p>

          {/* 5. Cookies */}
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3">
            {t('privacyCookies', lang)}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {t('privacyCookiesDesc', lang)}
          </p>

          {/* 6. 用户权利 */}
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3">
            {t('privacyUserRights', lang)}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {t('privacyUserRightsDesc', lang)}
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-6">
            <li>{t('privacyDataAccess', lang)}</li>
            <li>{t('privacyDataDeletion', lang)}</li>
          </ul>

          {/* 7. 数据安全 */}
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3">
            {t('privacyDataSecurity', lang)}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {t('privacyDataSecurityDesc', lang)}
          </p>

          {/* 8. 政策变更 */}
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3">
            {t('privacyPolicyChanges', lang)}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {t('privacyPolicyChangesDesc', lang)}
          </p>

          {/* 9. 联系我们 */}
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3">
            {t('privacyContact', lang)}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
            {t('privacyContactEmail', lang)}
          </p>
        </div>
      </main>

      {/* 页脚 */}
      <footer className="bg-white dark:bg-[#2D2D3F] border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            <p>{t('source', lang)} | {t('lastUpdate', lang)}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
